import {
  ApolloClient, InMemoryCache, ApolloLink, from, split,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { persistCache } from 'apollo3-cache-persist';
import { createLink } from 'apollo-absinthe-upload-link';
import { RetryLink } from '@apollo/client/link/retry';
import { enqueueSnackbar } from 'notistack';
import { getProgressSubscriber } from './progress';
import getProtocol from './getProtocol.mjs';
import getOrigin from './getOrigin';
import getApiUrl from './getApiUrl';
import cacheOptions from './cacheOptions';
import SseLink from './SseLink';
import inTest from './inTest';

const getRequestIdFromContext = (operation) => {
  const { response } = operation.getContext();
  return response?.headers?.get('x-request-id') ?? null;
};

let apolloClient = null;

export const errorHandler = ({ graphQLErrors, networkError, operation }) => {
  if (typeof window !== 'undefined') {
    const { passErrors = [] } = operation.getContext();
    const requestId = getRequestIdFromContext(operation);

    const filteredGraphQLErrors = graphQLErrors
      ?.filter(({ message }) => !passErrors.includes(message));

    if (filteredGraphQLErrors?.length > 0) {
      if (filteredGraphQLErrors[0].message === 'invalid_token') {
        localStorage.clear();

        window.location.reload();

        return;
      }

      const uniqueMessages = new Set(
        filteredGraphQLErrors
          .map((error) => error.message),
      );

      const messages = [...uniqueMessages].join(', ');
      const errorMessage = `${messages} (Request ID: ${requestId})`;

      enqueueSnackbar(errorMessage, { variant: 'error' });

      return;
    }

    if ([
      'CreateAnalyticsEvent',
      'GeocodeCountry',
    ].includes(operation.operationName)) {
      return;
    }

    if (networkError) {
      const { statusCode } = networkError;
      const prefix = statusCode
        ? `${statusCode >= 500 ? 'Server' : 'Request'} error (${statusCode})`
        : 'Network error';

      const errorMessage = (
        `${prefix}: ${networkError.message} (Request ID: ${requestId})`
      );

      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }
};

export const errorLink = onError(errorHandler);

const getAccessToken = () => (typeof localStorage === 'undefined'
  ? null : localStorage.getItem('accessToken'));

export const getSocketParameters = () => ({
  accessToken: getAccessToken(),
});

const getAuthorizationHeaders = () => {
  const accessToken = getAccessToken();

  return {
    authorization: accessToken ? `Bearer ${accessToken}` : null,
  };
};

function create(initialState, options = {}) {
  const { req: request = {}, reqUrl } = options;
  const { headers: requestHeaders, originalUrl } = request;
  const url = reqUrl || (request.get && `${
    getProtocol(request)}://${request.get('host')}${originalUrl}`);
  const apiUrl = getApiUrl(getOrigin(url));
  const httpLink = createLink({
    uri: apiUrl,
    getProgressSubscriber,
  });
  const sseLink = new SseLink({
    url: apiUrl,
    headers: getAuthorizationHeaders,
  });

  const ipAddressMiddleWare = new ApolloLink((operation, forward) => {
    if (requestHeaders) {
      const {
        'x-real-ip': xRealIp,
        'x-forwarded-for': xForwardedFor,
      } = requestHeaders;

      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          'x-real-ip': xRealIp,
          'x-forwarded-for': xForwardedFor,
        },
      }));
    }

    return forward(operation);
  });

  const authMiddleware = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...getAuthorizationHeaders(),
      },
    }));
    return forward(operation);
  });

  const apiSplitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition'
        && definition.operation === 'subscription'
      );
    },
    sseLink,
    httpLink,
  );

  const apiLink = from([
    errorLink,
    new RetryLink(),
    ipAddressMiddleWare,
    authMiddleware,
    apiSplitLink,
  ]);

  const cache = new InMemoryCache(cacheOptions);

  cache.restore({ ...initialState });

  if (typeof localStorage !== 'undefined') {
    persistCache({
      cache,
      storage: localStorage,
    });
  }

  const isBrowser = typeof window !== 'undefined';

  return new ApolloClient({
    connectToDevTools: isBrowser,
    ssrMode: !isBrowser,
    link: apiLink,
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
      query: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
}

export default function initApollo(initialState, options) {
  if (typeof window === 'undefined' || inTest) {
    return create(initialState, options);
  }

  if (!apolloClient) {
    apolloClient = create(initialState, options);
  }

  return apolloClient;
}
